import { useSuspenseQuery, useMutation, type UseMutationOptions } from '@tanstack/react-query';
import type dayjs from 'dayjs';
import {
  getStocks,
  postCreateNewStockItem,
  deleteStockItem,
  getStockItems,
  getStockAllowances,
  postAnalyzeReceipt,
  postStockIntakeItem,
} from './api';
import type {
  ReceiptIntakeItem,
  ExistingStockItem,
  Stock,
  StockIntakePayload,
  StockItemCreateData,
  StockWithStockItems,
} from './entity';
import { useAddSnackbarMessage } from '@shared/useAddSnackBar';
import { queryClient } from '@shared/queries';
import { GetStockAllowance } from '@shared/entity';
import { useIntlTranslations } from '@libs/intl/intl.hooks';
import { useAnalytics } from '@libs/mixpanel/useAnalytics';

export const useSuspenseStocksQuery = () => {
  return useSuspenseQuery<Stock[]>({
    queryKey: ['stocks'],
    queryFn: () => getStocks(),
    staleTime: 1000 * 60 * 20,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
};

export const useCreateOrUpdateStockItemMutation = (
  options?: UseMutationOptions<ExistingStockItem, unknown, StockItemCreateData>,
) => {
  const addSnackBarMessage = useAddSnackbarMessage();
  const { t } = useIntlTranslations('stock.mutation');
  const { trackOnboardingStep } = useAnalytics();

  return useMutation<ExistingStockItem, Error, StockItemCreateData>({
    mutationFn: (stockItem: StockItemCreateData) => postCreateNewStockItem(stockItem),
    async onSuccess(data, variables, context, meta) {
      if (!variables.id) {
        trackOnboardingStep('stockItemCreated', 6);
      }
      options?.onSuccess?.(data, variables, context, meta);

      queryClient.setQueriesData<StockWithStockItems[]>(
        { queryKey: ['stockItems'] },
        currentStocks => {
          if (!currentStocks) {
            return currentStocks;
          }

          return currentStocks.map(stock => {
            if (stock.id !== data.stockId) {
              return stock;
            }

            const itemIndex = stock.stockItems.findIndex(item => item.id === data.id);
            const stockItems = [...stock.stockItems];

            if (itemIndex >= 0) {
              stockItems[itemIndex] = { ...stockItems[itemIndex], ...data };
            } else {
              stockItems.push(data);
            }

            return { ...stock, stockItems };
          });
        },
      );

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['stockItems'], refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: ['logs'] }),
        queryClient.invalidateQueries({ queryKey: ['onboarding-progress'] }),
      ]);
      addSnackBarMessage({ text: t('upsertSuccess'), type: 'success' });
    },
    onError: () => {
      addSnackBarMessage({ text: t('upsertError'), type: 'error' });
    },
  });
};
export const useDeleteStockItemMutation = (options?: UseMutationOptions<void, unknown, string>) => {
  const addSnackBarMessage = useAddSnackbarMessage();
  const { t } = useIntlTranslations('stock.mutation');

  return useMutation({
    mutationFn: (stockItemId: string) => deleteStockItem(stockItemId),
    onSuccess(data, variables, context, meta) {
      options?.onSuccess?.(data, variables, context, meta);
      queryClient.invalidateQueries({ queryKey: ['stockItems'] });
      queryClient.invalidateQueries({ queryKey: ['onboarding-progress'] });
      addSnackBarMessage({ text: t('deleteSuccess'), type: 'success' });
    },
    onError: () => {
      addSnackBarMessage({ text: t('deleteError'), type: 'error' });
    },
  });
};

export const useAnalyzeReceiptMutation = (
  options?: UseMutationOptions<
    ReceiptIntakeItem[],
    unknown,
    { imageData: string; stockId?: string }
  >,
) => {
  const addSnackBarMessage = useAddSnackbarMessage();
  const { t } = useIntlTranslations('stock.mutation');

  return useMutation<ReceiptIntakeItem[], Error, { imageData: string; stockId?: string }>({
    mutationFn: ({ imageData, stockId }) => postAnalyzeReceipt(imageData, stockId),
    ...options,
    onError: () => {
      addSnackBarMessage({ text: t('receiptAnalyzeError'), type: 'error' });
    },
  });
};

export const useConfirmReceiptIntakeMutation = (
  options?: UseMutationOptions<unknown, unknown, StockIntakePayload[]>,
) => {
  const addSnackBarMessage = useAddSnackbarMessage();
  const { t } = useIntlTranslations('stock.mutation');

  return useMutation<unknown, Error, StockIntakePayload[]>({
    mutationFn: async (payloads: StockIntakePayload[]) => {
      for (const payload of payloads) {
        await postStockIntakeItem(payload);
      }
    },
    onSuccess(data, variables, context, meta) {
      options?.onSuccess?.(data, variables, context, meta);
      queryClient.invalidateQueries({ queryKey: ['stockItems'] });
      queryClient.invalidateQueries({ queryKey: ['logs'] });
      queryClient.invalidateQueries({ queryKey: ['onboarding-progress'] });
      addSnackBarMessage({ text: t('receiptIntakeSuccess'), type: 'success' });
    },
    onError: () => {
      addSnackBarMessage({ text: t('receiptIntakeError'), type: 'error' });
    },
  });
};

export const getStockItemsQuery = (stockId: string | undefined) => {
  return {
    queryKey: ['stockItems', stockId],
    queryFn: () => {
      return getStockItems(stockId ?? '');
    },
  };
};

export const useStockItemsQuery = (stockId: string | undefined) => {
  return useSuspenseQuery<StockWithStockItems[]>({
    ...getStockItemsQuery(stockId),
    staleTime: 1000 * 60 * 20,
    refetchOnMount: false,
  });
};

export const useStockAllowancesQuery = ({
  teamId,
  fromDate,
  toDate,
}: {
  teamId: string | undefined;
  fromDate: dayjs.Dayjs;
  toDate: dayjs.Dayjs;
}) => {
  return useSuspenseQuery<GetStockAllowance[]>({
    queryKey: ['stockAllowances', teamId, fromDate, toDate],
    queryFn: () => {
      if (!teamId) {
        return Promise.resolve([]);
      }

      return getStockAllowances({ teamId, fromDate, toDate });
    },
  });
};
